import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@patient-watch/ui/accordion";
import type { EntryValueViewT, PageViewT, SizeT } from "@patient-watch/types";
import { cn } from "../../../lib/utils";
import { sortEntryValuesView } from "../../entries/services/sortEntryValuesView";
import { groupEntryValuesByPage, shouldShowPageHeadings } from "../entryValuesPageGroups";
import {
    partitionEntryValuesForHistoryDisplay,
    shouldDisplayEntryValue,
    sortHeadlineEntryValues,
} from "../entryValueDisplay";
import HeadlineEntryValues from "./HeadlineEntryValues";
import { EntryValuesQuestionGroups, questionRowClass } from "./EntryValuesQuestionGroups";
import { EntryValueSwitch } from "./EntryValueSwitch";
import { CheckboxGroupEntryValue } from "./input-types/CheckboxGroupEntryValue";
import { JoinedMeasurementEntryValue } from "./input-types/JoinedMeasurementEntryValue";
import { withoutStandaloneUnitChoices } from "../measurementRuns";
import { buildImageCountGroups } from "../imageCountGroups";
import { ImageCountsEntryValue } from "./ImageCountsEntryValue";

export type EntryCardEntryValuesVariant = "default" | "plain";

export interface EntryCardEntryValuesProps {
    size?: SizeT;
    entryValues: EntryValueViewT[];
    diaryId?: string;
    /** When uncontrolled, start with the questions accordion open */
    defaultExpanded?: boolean;
    /** Plain list without per-value borders or padding (history cards). */
    variant?: EntryCardEntryValuesVariant;
    /** The form's pages. Lets image-and-count answers show their pictures. */
    pages?: PageViewT[] | null;
}

function otherValueItemClass(size: SizeT | undefined, variant: EntryCardEntryValuesVariant): string {
    if (variant === "plain") {
        return questionRowClass(variant);
    }
    return cn(
        "flex flex-row gap-4 dark:bg-slate-800/50 p-2 border border-slate-200 rounded-md font-medium text-slate-700",
        size === "sm" ? "text-sm" : "text-md"
    );
}

function fieldSwitchSize(variant: EntryCardEntryValuesVariant): "sm" | undefined {
    return variant === "plain" ? "sm" : undefined;
}

const EntryCardEntryValues = ({
    size,
    entryValues,
    diaryId,
    defaultExpanded = false,
    variant = "default",
    pages,
}: EntryCardEntryValuesProps) => {
    const validEntryValues = entryValues?.filter(shouldDisplayEntryValue) ?? [];

    if (!validEntryValues.length) return null;

    const { headlineValues, questionValues, showAllWithoutSplit } =
        partitionEntryValuesForHistoryDisplay(validEntryValues);
    const chartValues = sortHeadlineEntryValues(headlineValues);
    const otherValues = sortEntryValuesView(withoutStandaloneUnitChoices(questionValues));

    const shouldAlwaysShow = showAllWithoutSplit || otherValues.length < 3;
    const formName = validEntryValues.find((ev) => ev.form_name)?.form_name;
    const groupedByPage = groupEntryValuesByPage(otherValues);
    const showPageHeadings = shouldShowPageHeadings(groupedByPage);
    const imageCountGroups = buildImageCountGroups(otherValues, pages);

    const renderOtherRow = (ev: EntryValueViewT) => (
        <div className={otherValueItemClass(size, variant)}>
            <EntryValueSwitch size={fieldSwitchSize(variant)} entryValue={ev} diaryId={diaryId} />
        </div>
    );

    const renderCheckboxGroup = (values: EntryValueViewT[]) => (
        <div className={otherValueItemClass(size, variant)}>
            <CheckboxGroupEntryValue entryValues={values} size={fieldSwitchSize(variant)} />
        </div>
    );

    const renderMeasurementGroup = (values: EntryValueViewT[]) => (
        <div className={otherValueItemClass(size, variant)}>
            <JoinedMeasurementEntryValue entryValues={values} size={fieldSwitchSize(variant)} />
        </div>
    );

    const renderQuestionsContent = () => (
        <EntryValuesQuestionGroups
            groupedByPage={groupedByPage}
            showPageHeadings={showPageHeadings}
            variant={variant}
            flatValues={otherValues}
            renderRow={renderOtherRow}
            renderCheckboxGroup={renderCheckboxGroup}
            renderMeasurementGroup={renderMeasurementGroup}
            imageCountGroups={imageCountGroups}
            renderImageCountGroup={(group) => <ImageCountsEntryValue group={group} size={size} />}
        />
    );

    return (
        <div
            className={cn("flex flex-col gap-2", variant === "plain" && "gap-1.5 [&_[data-entry-value-root]]:py-0")}
            data-testid="entry-values"
        >
            {variant !== "plain" && formName ? (
                <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm">{formName}</h3>
            ) : null}
            <ol className={cn("flex flex-col", variant === "plain" ? "gap-1.5" : "gap-2")}>
                {chartValues.length > 0 ? (
                    <li className="w-full list-none">
                        <HeadlineEntryValues entryValues={chartValues} variant={variant} size={size} />
                    </li>
                ) : null}

                {otherValues.length > 0 ? (
                    <li className={cn("list-none", variant === "plain" && !shouldAlwaysShow && "mt-1")}>
                        {shouldAlwaysShow ? (
                            renderQuestionsContent()
                        ) : (
                            <Accordion
                                type="single"
                                collapsible
                                defaultValue={defaultExpanded ? "questions" : undefined}
                                className="w-full"
                            >
                                <AccordionItem value="questions" className="border-0">
                                    <AccordionTrigger
                                        className={cn(
                                            "py-2 font-normal hover:no-underline",
                                            variant === "plain"
                                                ? "text-slate-600 dark:text-slate-400 text-sm"
                                                : "text-slate-600 dark:text-slate-400 text-sm"
                                        )}
                                    >
                                        Questions ({otherValues.length})
                                    </AccordionTrigger>
                                    <AccordionContent className="pb-0">{renderQuestionsContent()}</AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        )}
                    </li>
                ) : null}
            </ol>
        </div>
    );
};

export default EntryCardEntryValues;
